import { useEffect, useState } from 'react';

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'penrose-theme';

function readStoredTheme(): Theme {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'dark' || v === 'light') return v;
  } catch { /* localStorage may be unavailable (SSR / privacy mode) */ }
  return 'light';
}

function applyTheme(theme: Theme) {
  document.documentElement.setAttribute('data-theme', theme);
  try { localStorage.setItem(STORAGE_KEY, theme); } catch { /* ignore */ }
}

/**
 * Apply the stored theme to <html data-theme="..."> synchronously, before
 * React renders. Called once from main.tsx so every page — including the
 * one the user lands on via direct URL or a hard refresh — picks up the
 * preference without a flash of the wrong theme.
 */
export function bootTheme(): void {
  applyTheme(readStoredTheme());
}

export function useTheme(): [Theme, (t: Theme) => void] {
  // Initial value matches what bootTheme() wrote to <html>, so the first
  // render is in the right theme without a FOUC.
  const [theme, setThemeState] = useState<Theme>(readStoredTheme);

  useEffect(() => {
    // Defensive: in case bootTheme() didn't run (e.g. some test harness),
    // make sure the attribute matches state on mount.
    applyTheme(theme);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setTheme = (t: Theme) => {
    setThemeState(t);
    applyTheme(t);
  };

  return [theme, setTheme];
}
